import { z } from 'zod';

/** Business-model version, independent of the FA phase migration generation. */
export const DATA_CENTER_FINANCIAL_MODEL_KIND = 'data-center-capacity-fee' as const;
const amount = z.number().finite().nonnegative().nullable();
const percent = z.number().finite().min(0).max(100).nullable();
const hash = z.string().regex(/^[a-f0-9]{64}$/);
const id = z.string().min(1).refine(value => !value.includes('/'));

export const dataCenterFinancialSourceSchema = z.object({
  documentId: id,
  rawSha256: hash,
  bodySha256: hash,
  scenarioId: z.string().min(1).optional(),
}).strict();

export const dataCenterFinancialQuoteSchema = z.object({
  documentId: id,
  bodySha256: hash,
  quote: z.string().min(1),
  charStart: z.number().int().nonnegative(),
  charEnd: z.number().int().positive(),
  page: z.number().int().positive().optional(),
}).strict().refine(q => q.charEnd - q.charStart === q.quote.length, 'Quote offsets must use exact UTF-16 source length.');

export const dataCenterFinancialPeriodSchema = z.object({
  year: z.number().int().min(1900).max(2300),
  availableItMw: amount,
  occupancyPct: percent,
  energyUtilizationPct: percent,
  pue: z.number().finite().min(1).nullable(),
  energyHours: z.number().finite().min(0).max(8784).nullable(),
  billingMonths: z.number().finite().min(0).max(12).nullable(),
  capacityFeeUsdPerItKwMonth: amount,
  billedFacilityPeakMw: amount,
  gridEnergyUsdPerKwh: amount,
  demandChargeUsdPerFacilityKwMonth: amount,
  fixedOperatingUsd: amount,
  commissionedCapexPct: percent,
  maintenancePct: percent,
  standbyFuelLitres: amount,
  standbyFuelUsdPerLitre: amount,
  constructionDrawPct: percent,
  overhaulUsd: amount,
  terminalProceedsUsd: amount,
}).strict();

/** Each entered field is either a literal/derived source reading or an explicit manual assumption. */
const provenance = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('source'), quoteIds: z.array(z.string().min(1)).min(1), derivation: z.string().min(1).optional() }).strict(),
  z.object({ kind: z.literal('manual-assumption'), note: z.string().trim().min(1) }).strict(),
]);

export const dataCenterFinancialModelV1Schema = z.object({
  schemaVersion: z.literal(1),
  kind: z.literal(DATA_CENTER_FINANCIAL_MODEL_KIND),
  ownerUid: id,
  siteId: id,
  currency: z.literal('USD'),
  priceBasis: z.literal('constant-dollars'),
  priceYear: z.number().int().min(1900).max(2300).nullable(),
  cashflowBasis: z.literal('unlevered-pre-tax'),
  referenceYear: z.number().int().min(1900).max(2300).nullable(),
  discountRatePct: z.number().finite().min(0).max(100).nullable(),
  pricedBomUsd: amount,
  contingencyPct: percent,
  electricityIncludedInCapacityFee: z.literal(true),
  authority: z.enum(['fictional-scenario', 'manual-project-input']),
  scenarioId: z.string().min(1).optional(),
  /** Known subtotal is useful arithmetic; an unpriced scope never becomes a complete budget. */
  unpricedScopes: z.array(z.string().trim().min(1)),
  decisionLimitations: z.array(z.string().trim().min(1)).min(1),
  sources: z.array(dataCenterFinancialSourceSchema),
  quotes: z.record(z.string(), dataCenterFinancialQuoteSchema),
  fieldProvenance: z.record(z.string(), provenance),
  periods: z.array(dataCenterFinancialPeriodSchema).max(150),
  /** Source comparisons are never inputs to any calculation or verdict. */
  sourceComparison: z.object({
    npvUsd: z.number().finite().nullable(),
    irrPct: z.number().finite().nullable(),
    paybackYears: amount,
    quoteIds: z.array(z.string().min(1)),
  }).strict().optional(),
}).strict().superRefine((model, ctx) => {
  const issue = (message: string) => ctx.addIssue({ code: z.ZodIssueCode.custom, message });
  const ids = model.sources.map(source => source.documentId);
  if (new Set(ids).size !== ids.length) issue('Source documents must be unique.');
  if (model.authority === 'fictional-scenario' && !model.scenarioId) issue('Fictional financial inputs require their explicit scenario identity.');
  for (const source of model.sources) {
    if (model.authority === 'fictional-scenario' && source.scenarioId !== model.scenarioId) issue('Financial source scenario mismatch.');
  }
  for (const [quoteId, quote] of Object.entries(model.quotes)) {
    if (!model.sources.some(source => source.documentId === quote.documentId && source.bodySha256 === quote.bodySha256)) issue(`Quote ${quoteId} has no matching frozen source identity.`);
  }
  for (const [field, evidence] of Object.entries(model.fieldProvenance)) {
    if (evidence.kind === 'source' && evidence.quoteIds.some(q => !model.quotes[q])) issue(`Source lineage missing for ${field}.`);
  }
  if (model.sourceComparison && (model.sourceComparison.quoteIds.some(q => !model.quotes[q])
    || ([model.sourceComparison.npvUsd, model.sourceComparison.irrPct, model.sourceComparison.paybackYears].some(value => value != null)
      && model.sourceComparison.quoteIds.length === 0))) issue('Source comparison lineage missing.');
  for (const field of ['priceYear', 'referenceYear', 'discountRatePct', 'pricedBomUsd', 'contingencyPct'] as const) {
    if (model[field] != null && !model.fieldProvenance[field]) issue(`Input provenance missing for ${field}.`);
  }
  for (const period of model.periods) {
    for (const [field, value] of Object.entries(period)) {
      if (field !== 'year' && value != null && !model.fieldProvenance[`periods.${period.year}.${field}`]) issue(`Input provenance missing for ${period.year}.${field}.`);
    }
  }
  if (model.periods.slice(0, -1).some(period => (period.terminalProceedsUsd ?? 0) > 0)) issue('Terminal proceeds belong only to the final annual period.');
  const years = model.periods.map(p => p.year);
  if (years.some((year, i) => i > 0 && year !== years[i - 1]! + 1)) issue('Periods must be ordered, unique, contiguous calendar years.');
  if (model.referenceYear != null && years.length && years[0] !== model.referenceYear) issue('The first annual period must be the explicit valuation reference year.');
});

export type DataCenterFinancialModelV1 = z.infer<typeof dataCenterFinancialModelV1Schema>;
export type DataCenterFinancialPeriodV1 = z.infer<typeof dataCenterFinancialPeriodSchema>;

export interface DataCenterFinancialLedgerRow {
  year: number;
  contractedItKw: number;
  gridConsumptionMwh: number;
  capacityRevenueUsd: number;
  gridEnergyUsd: number;
  demandChargeUsd: number;
  fixedOperatingUsd: number;
  maintenanceUsd: number;
  standbyFuelUsd: number;
  constructionDrawUsd: number;
  overhaulUsd: number;
  terminalProceedsUsd: number;
  operatingCashUsd: number;
  netCashUsd: number;
  cumulativeCashUsd: number;
}

export interface DataCenterFinancialResult {
  modelKind: typeof DATA_CENTER_FINANCIAL_MODEL_KIND;
  modelHash: string | null;
  computable: boolean;
  issues: string[];
  ledger: DataCenterFinancialLedgerRow[];
  modeledCapexUsd: number | null;
  npvUsd: number | null;
  irrPct: number | null;
  irrUnavailableReason?: 'one-sided-cashflow' | 'non-conventional-cashflow' | 'no-convergence';
  paybackYears: number | null;
  paybackMethod: 'undiscounted-first-cumulative-crossing-linear-within-period';
  paybackClock: 'elapsed-years-from-reference-year-including-construction';
}
